import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Typography, Box, CircularProgress, Alert, IconButton, Tooltip } from "@mui/material";
import FlexColumnBox from "../../components/common/FlexBox";
import DataComparisonGrid from "../../components/common/DataComparisonGrid";
import WeekDatePicker from "../../components/common/WeekDatePicker";
import HotelSelector from "../../components/common/HotelSelector";
import HotelDataStatusList from "../../components/common/HotelDataStatusList";
import { useHotels } from "../../hooks/useHotels";
import { useAuth } from "../../context/authContext";
import { useDebounce } from "../../utils/useDebounce";
import StatService from "../../api/statService";
import { addDaysToDate, getLastFriday, getNextPeriod, resetToFriday } from "../../utils/dateUtils";
import WeeklyForecastTable from "../../components/common/WeeklyForecastTable";
import { buildGrillesAvtCsv } from "../../utils/grillesCsv";
import { downloadCsv, formatCsvFilename } from "../../utils/csvDownload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { exportGrillesCompletXlsx } from "../../utils/grillesXlsx";

type NamedData = { name: string; data: string | number };
type ComparisonData = {
  forecastData: { current: NamedData[]; previous: NamedData[] };
  realData: { current: NamedData[]; previous: NamedData[] };
  visitorData: { current: NamedData[]; previous: NamedData[] };
};

// Fonction pour filtrer les items selon la période de prévision appropriée
// Pour les prévisions glissantes sur 2 semaines :
// - Period 1 = prévision pour la semaine courante (jours 0-6)
// - Period 2 = prévision pour la semaine suivante (jours 7-13)
// Retourne un item par hotel avec la période correspondant au jour demandé
const filterByPeriod = (items: any[], dayOffset: number): any[] => {
  const itemsByHotel = new Map<number, any>();

  const targetPeriod = dayOffset < 7 ? 1 : 2;
  
  items.forEach(item => {
    const hotelId = item.fK_Hotel_ID;
    const existing = itemsByHotel.get(hotelId);
    const itemPeriod = item.period ?? 1;
    
    if (!existing) {
      itemsByHotel.set(hotelId, item);
    } else {
      const existingPeriod = existing.period ?? 1;
      if (itemPeriod === targetPeriod && existingPeriod !== targetPeriod) {
        itemsByHotel.set(hotelId, item);
      }
      else if (itemPeriod === existingPeriod && itemPeriod > existingPeriod) {
        itemsByHotel.set(hotelId, item);
      }
    }
  });
  return Array.from(itemsByHotel.values());
};

const formatWeeklyRows = (data: any[], start: Date, startDayOffset: number = 0) => {
  const rows = [];
  for (let i = 0; i < 7; i++) {
    const dayOffset = startDayOffset + i;
    const d = addDaysToDate(start, dayOffset);
    const dateStr = d.toISOString().split("T")[0];
    const dailyItems = data.filter((x) => x.date === dateStr);
    const filteredItems = filterByPeriod(dailyItems, dayOffset);
    
    if (filteredItems.length === 0) {
      rows.push({
        nights: "N/A",
        people: "N/A",
        occupancyRate: "N/A",
      });
    } else {
      const sold = filteredItems.reduce(
        (sum, item) => sum + (item.sold_Units || 0),
        0
      );
      const available = filteredItems.reduce(
        (sum, item) => sum + (item.available_Units || 0),
        0
      );
      const visitors = filteredItems.reduce(
        (sum, item) => sum + (item.number_Of_Visitors || 0),
        0
      );
      rows.push({
        nights: sold,
        people: visitors,
        occupancyRate: available > 0 ? Number(((sold / available) * 100).toFixed(2)) : 0,
      });
    }
  }
  return rows;
};

const buildVisitorData = (visitors: any[]): NamedData[] => {
  const map = new Map<string, number>();
  visitors.forEach((v) => {
    const name = v.visitorTypeName?.trim() || `Type ${v.id}`;
    map.set(name, (map.get(name) ?? 0) + v.total);
  });
  const list: NamedData[] = Array.from(map.entries()).map(([k, v]) => ({
    name: k,
    data: v,
  }));
  const total = visitors.reduce((sum, v) => sum + (v.total || 0), 0);
  list.push({ name: "Total visiteurs", data: total });
  return list;
};

const GrillesAVT = () => {
  const REAL_DATA_OFFSET = -14;
  const hotels = useHotels();
  const { user } = useAuth();

  const initialStart = getNextPeriod(new Date);

  const [localStartDate, setLocalStartDate] = useState(initialStart);
  
  const [startDate, setStartDate] = useState(addDaysToDate(initialStart, REAL_DATA_OFFSET));
  const [selectedHotel, setSelectedHotel] = useState<any | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyDate = useCallback((date: Date) => {
    setStartDate(addDaysToDate(date, REAL_DATA_OFFSET));
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const debouncedSetDate = useCallback((date: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setStartDate(addDaysToDate(date, REAL_DATA_OFFSET));
    }, 3000);
  }, []);
  const [comparisonData, setComparisonData] = useState<ComparisonData>({
    forecastData: { current: [], previous: [] },
    realData: { current: [], previous: [] },
    visitorData: { current: [], previous: [] },
  });
  const [weeklyOccupancyData, setWeeklyOccupancyData] = useState<any[]>([]);
  const [weeklyForecastData, setWeeklyForecastData] = useState<any[]>([]);
  const [forecastComparisonWeek1, setForecastComparisonWeek1] = useState<any>(null);
  const [forecastComparisonWeek2, setForecastComparisonWeek2] = useState<any>(null);
  const [comparisonTableData, setComparisonTableData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestCounter = useRef(0);
  const currentYear = useMemo(() => startDate.getFullYear(), [startDate]);
  const [hotelDataStatus, setHotelDataStatus] = useState<
    Array<{ id: number; name: string; hasData: boolean, warning: boolean }>
  >([]);

  useEffect(() => {
    // If user has an associated hotel (non-admin), lock to that
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
      return;
    }
    // If admin or user without hotel, show all (null => all hotels)
    if (user && (!user.hotel || user.role === "Admin")) {
      setSelectedHotel(null);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    const currentRequest = ++requestCounter.current;
    setLoading(true);
    setError(null);
    // Clear previous data immediately to avoid showing stale content when new query yields no rows
    setWeeklyOccupancyData([]);
    setWeeklyForecastData([]);
    setComparisonTableData(null);
    setHotelDataStatus([]);
    setComparisonData({
      forecastData: { current: [], previous: [] },
      realData: { current: [], previous: [] },
      visitorData: { current: [], previous: [] },
    });
    
    const forecastStartDate = addDaysToDate(startDate, 14);
    const endDate = addDaysToDate(startDate, 6);
    const prevStart = addDaysToDate(startDate, -365);
    const prevEnd = addDaysToDate(endDate, -365);
    // Pour les prévisions, on récupère 2 semaines (14 jours)
    const forecastEndDate = addDaysToDate(forecastStartDate, 13);
    // Pour la comparaison de la semaine 2, on a besoin de l'année précédente
    const prevForecastStart = addDaysToDate(forecastStartDate, -365);
    const prevForecastEnd = addDaysToDate(forecastEndDate, -365);

    try {
      const [forecast, forecastPrev, realNow, realPrev, visitorsNow, visitorsPrev, forecastToCompare] =
        await Promise.all([
          StatService.getForecast(
            selectedHotel?.id ?? null,
            forecastStartDate,
            forecastEndDate
          ),
          StatService.getForecast(
            selectedHotel?.id ?? null,
            prevForecastStart,
            prevForecastEnd
          ),
          StatService.getOccupation(
            selectedHotel?.id ?? null,
            startDate,
            endDate
          ),
          StatService.getOccupation(
            selectedHotel?.id ?? null,
            prevStart,
            prevEnd
          ),
          StatService.getVisitors(
            selectedHotel?.id ?? null,
            startDate,
            endDate
          ),
          StatService.getVisitors(
            selectedHotel?.id ?? null,
            prevStart,
            prevEnd
          ),
          StatService.getForecast(
            selectedHotel?.id ?? null,
            startDate,
            endDate
          )
        ]);

      if (currentRequest !== requestCounter.current) return;

      const getTotals = (
        data: any[],
        referenceStartDate: Date,
        startDayOffset: number = 0
      ) => {
        let occupied = 0;
        let available = 0;
        let visitors = 0;
        let hasData = false;

        for (let i = 0; i < 7; i++) {
          const dayOffset = startDayOffset + i;
          const d = addDaysToDate(referenceStartDate, dayOffset)
            .toISOString()
            .split("T")[0];
          const dayItems = data.filter((x) => x.date === d);
          const filteredItems = filterByPeriod(dayItems, dayOffset);
          
          if (filteredItems.length > 0) {
            hasData = true;
            filteredItems.forEach((item) => {
              occupied += item.sold_Units ?? 0;
              available += item.available_Units ?? 0;
              visitors += item.number_Of_Visitors ?? 0;
            });
          }
        }

        return { occupied, available, visitors, hasData };
      };

      const currentTotals = getTotals(realNow, startDate);
      const previousTotals = getTotals(realPrev, prevStart);

      setComparisonTableData({
        availableNights: {
          current: currentTotals.hasData ? currentTotals.available : "N/A",
          previous: previousTotals.hasData ? previousTotals.available : "N/A",
        },
        occupiedNights: {
          current: currentTotals.hasData ? currentTotals.occupied : "N/A",
          previous: previousTotals.hasData ? previousTotals.occupied : "N/A",
        },
        persons: {
          current: currentTotals.hasData ? currentTotals.visitors : "N/A",
          previous: previousTotals.hasData ? previousTotals.visitors : "N/A",
        },
        averageOccupancy: {
          current: currentTotals.hasData && currentTotals.available > 0
            ? Number(((currentTotals.occupied / currentTotals.available) * 100).toFixed(2))
            : currentTotals.hasData ? 0 : "N/A",
          previous: previousTotals.hasData && previousTotals.available > 0
            ? Number(((previousTotals.occupied / previousTotals.available) * 100).toFixed(2))
            : previousTotals.hasData ? 0 : "N/A",
        },
      });

      setWeeklyOccupancyData(
        formatWeeklyRows(realNow, startDate)
      );

      // Formater les données de prévisions sur 2 semaines (14 jours)
      const forecastRows = [];
      for (let i = 0; i < 14; i++) {
        const d = addDaysToDate(forecastStartDate, i);
        const dateStr = d.toISOString().split("T")[0];
        const dailyItems = forecast.filter((x) => x.date === dateStr);
        const filteredItems = filterByPeriod(dailyItems, i);
        
        if (filteredItems.length === 0) {
          forecastRows.push({
            nights: "N/A",
            people: "N/A",
            occupancyRate: "N/A",
          });
        } else {
          const sold = filteredItems.reduce(
            (sum, item) => sum + (item.sold_Units || 0),
            0
          );
          const available = filteredItems.reduce(
            (sum, item) => sum + (item.available_Units || 0),
            0
          );
          const visitors = filteredItems.reduce(
            (sum, item) => sum + (item.number_Of_Visitors || 0),
            0
          );
          forecastRows.push({
            nights: sold,
            people: visitors,
            occupancyRate: available > 0 ? Number(((sold / available) * 100).toFixed(2)) : 0,
          });
        }
      }
      setWeeklyForecastData(forecastRows);

      // Calculer les données de comparaison pour les prévisions
      // startDayOffset permet de gérer Week1 (0-6) et Week2 (7-13)
      const getForecastTotals = (
        data: any[],
        referenceStartDate: Date,
        dayCount: number,
        startDayOffset: number = 0
      ) => {
        let occupied = 0;
        let available = 0;
        let visitors = 0;
        let hasData = false;

        for (let i = 0; i < dayCount; i++) {
          const dayOffset = startDayOffset + i;
          const d = addDaysToDate(referenceStartDate, dayOffset)
            .toISOString()
            .split("T")[0];
          const dayItems = data.filter((x) => x.date === d);
          const filteredItems = filterByPeriod(dayItems, dayOffset);
          
          if (filteredItems.length > 0) {
            hasData = true;
          }
          
          const dayOccupied = filteredItems.reduce((sum, item) => sum + (item.sold_Units ?? 0), 0);
          const dayAvailable = filteredItems.reduce((sum, item) => sum + (item.available_Units ?? 0), 0);
          const dayVisitors = filteredItems.reduce((sum, item) => sum + (item.number_Of_Visitors ?? 0), 0);

          occupied += dayOccupied;
          available += dayAvailable;
          visitors += dayVisitors;
        }

        return { occupied, available, visitors, hasData };
      };

      // Semaine 1 : jours 0-6 depuis startDate (utilise Period 1)
      const week1CurrentTotals = getForecastTotals(forecast, forecastStartDate, 7, 0);
      const week1PreviousTotals = getForecastTotals(forecastPrev, prevForecastStart, 7, 0);

      setForecastComparisonWeek1({
        availableNights: {
          current: week1CurrentTotals.hasData ? week1CurrentTotals.available : "N/A",
          previous: week1PreviousTotals.hasData ? week1PreviousTotals.available : "N/A",
        },
        occupiedNights: {
          current: week1CurrentTotals.hasData ? week1CurrentTotals.occupied : "N/A",
          previous: week1PreviousTotals.hasData ? week1PreviousTotals.occupied : "N/A",
        },
        persons: {
          current: week1CurrentTotals.hasData ? week1CurrentTotals.visitors : "N/A",
          previous: week1PreviousTotals.hasData ? week1PreviousTotals.visitors : "N/A",
        },
        averageOccupancy: {
          current: week1CurrentTotals.hasData && week1CurrentTotals.available > 0
            ? Number(((week1CurrentTotals.occupied / week1CurrentTotals.available) * 100).toFixed(2))
            : week1CurrentTotals.hasData ? 0 : "N/A",
          previous: week1PreviousTotals.hasData && week1PreviousTotals.available > 0
            ? Number(((week1PreviousTotals.occupied / week1PreviousTotals.available) * 100).toFixed(2))
            : week1PreviousTotals.hasData ? 0 : "N/A",
        },
      });

      // Semaine 2 : jours 7-13 depuis startDate (utilise Period 2)
      const week2CurrentTotals = getForecastTotals(forecast, forecastStartDate, 7, 7);
      const week2PreviousTotals = getForecastTotals(forecastPrev, prevForecastStart, 7, 7);

      setForecastComparisonWeek2({
        availableNights: {
          current: week2CurrentTotals.hasData ? week2CurrentTotals.available : "N/A",
          previous: week2PreviousTotals.hasData ? week2PreviousTotals.available : "N/A",
        },
        occupiedNights: {
          current: week2CurrentTotals.hasData ? week2CurrentTotals.occupied : "N/A",
          previous: week2PreviousTotals.hasData ? week2PreviousTotals.occupied : "N/A",
        },
        persons: {
          current: week2CurrentTotals.hasData ? week2CurrentTotals.visitors : "N/A",
          previous: week2PreviousTotals.hasData ? week2PreviousTotals.visitors : "N/A",
        },
        averageOccupancy: {
          current: week2CurrentTotals.hasData && week2CurrentTotals.available > 0
            ? Number(((week2CurrentTotals.occupied / week2CurrentTotals.available) * 100).toFixed(2))
            : week2CurrentTotals.hasData ? 0 : "N/A",
          previous: week2PreviousTotals.hasData && week2PreviousTotals.available > 0
            ? Number(((week2PreviousTotals.occupied / week2PreviousTotals.available) * 100).toFixed(2))
            : week2PreviousTotals.hasData ? 0 : "N/A",
        },
      });

      // Pour la comparaison prévision vs réel, utiliser Week1 avec Period 1
      const forecastWeek1Only = forecastToCompare.filter((f) => {
        const fDate = new Date(f.date);
        const dayDiff = Math.floor((fDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        return dayDiff >= 0 && dayDiff < 7;
      });

      const uniqueForecastWeek1 = new Map<string, any>();
      forecastWeek1Only.forEach(item => {
        const key = `${item.fK_Hotel_ID}-${item.date}`;
        const existing = uniqueForecastWeek1.get(key);
        const itemPeriod = item.period ?? 1;
        const existingPeriod = existing?.period ?? 1;
        
        if (!existing || itemPeriod === 1 || (itemPeriod === existingPeriod && itemPeriod > existingPeriod)) {
          uniqueForecastWeek1.set(key, item);
        }
      });
      
      const deduplicatedForecastWeek1 = Array.from(uniqueForecastWeek1.values());
      const hasForecastWeek1Data = deduplicatedForecastWeek1.length > 0;

      const forecastWeek1Sold = deduplicatedForecastWeek1.reduce((acc, f) => acc + (f.sold_Units || 0), 0);
      const forecastWeek1Available = deduplicatedForecastWeek1.reduce((acc, f) => acc + (f.available_Units || 0), 0);

      setComparisonData({
        forecastData: {
          current: [
            {
              name: "Nuitées prévues",
              data: hasForecastWeek1Data ? forecastWeek1Sold : "N/A",
            },
            {
              name: "Taux prévu",
              data: hasForecastWeek1Data && forecastWeek1Available > 0
                  ? `${((forecastWeek1Sold / forecastWeek1Available) * 100).toFixed(2)}%`
                  : hasForecastWeek1Data ? "0%" : "N/A",
            },
          ],
          previous: [
            { name: "Nuitées réelles", data: currentTotals.hasData ? currentTotals.occupied : "N/A" },
            {
              name: "Taux réel",
              data: currentTotals.hasData && currentTotals.available > 0
                  ? `${((currentTotals.occupied / currentTotals.available) * 100).toFixed(2)}%`
                  : currentTotals.hasData ? "0%" : "N/A",
            },
          ],
        },
        realData: {
          current: [
            { name: "Nuitées réelles", data: currentTotals.hasData ? currentTotals.occupied : "N/A" },
            {
              name: "Taux réel",
              data: currentTotals.hasData && currentTotals.available > 0
                  ? `${((currentTotals.occupied / currentTotals.available) * 100).toFixed(2)}%`
                  : currentTotals.hasData ? "0%" : "N/A",
            },
          ],
          previous: [
            { name: "Nuitées N-1", data: previousTotals.hasData ? previousTotals.occupied : "N/A" },
            {
              name: "Taux N-1",
              data: previousTotals.hasData && previousTotals.available > 0
                  ? `${((previousTotals.occupied / previousTotals.available) * 100).toFixed(2)}%`
                  : previousTotals.hasData ? "0%" : "N/A",
            },
          ],
        },
        visitorData: {
          current: buildVisitorData(visitorsNow),
          previous: buildVisitorData(visitorsPrev),
        },
      });

      if (!selectedHotel && hotels.length > 0) {
        const statusList = hotels.map((hotel) => {
          const hotelForecastData = forecast.filter((item: any) => item.fK_Hotel_ID === hotel.id && item.fK_Hotel_ID > 0);
          const hotelRealData = realNow.filter((item: any) => item.fK_Hotel_ID === hotel.id && item.fK_Hotel_ID > 0);
          const hotelVisitorData = visitorsNow.filter((item: any) => item.hotelId === hotel.id && item.hotelId > 0);
          
          const warn = hotelForecastData.some((item) => item.sold_Units === 0) || hotelRealData.some((item) => item.sold_Units === 0)
            || hotelVisitorData.some((item : any) => item.total === 0);

          return {
            id: hotel.id,
            name: hotel.name,
            // Prévue = 2 semaines, réelle et visiteurs = 1 semaine
            hasData: hotelForecastData.length >= 14 && hotelRealData.length >= 7,
            warning : warn
          };
        });
        setHotelDataStatus(statusList);
      }
    } catch (error) {
      if (currentRequest === requestCounter.current) {
        console.error("Erreur lors de la récupération des données :", error);
        setError("Impossible de charger les données.");
      }
    } finally {
      if (currentRequest === requestCounter.current) {
        setLoading(false);
      }
    }
  }, [startDate, selectedHotel, hotels]);

  const debouncedFetch = useDebounce(fetchData, 200);

  useEffect(() => {
    debouncedFetch();
  }, [selectedHotel, startDate, debouncedFetch]);

  const handleExportCsv = () => {
    const includedHotelNames = selectedHotel
      ? [selectedHotel.name]
      : hotels.map((h) => h.name);

    const csv = buildGrillesAvtCsv({
      currentYear,
      startDate,
      comparisonData,
      weeklyOccupancyData,
      weeklyComparisonData: comparisonTableData,
      includedHotelNames,
    });

    const filename = formatCsvFilename("grilles_avt", startDate, addDaysToDate(startDate, 6));
    downloadCsv(filename, csv);
  };

  const handleExportXlsx = async () => {
    const excelStartDate = addDaysToDate(startDate, 14);
    try {
      const includedHotelNames = selectedHotel
        ? [selectedHotel.name]
        : hotelDataStatus
            .filter(h => h.hasData)
            .map(h => h.name);
      
      await exportGrillesCompletXlsx({
        startDate: excelStartDate,
        endDate: addDaysToDate(excelStartDate, 6),
        weeklyForecastData,
        weeklyOccupancyData,
        comparisonWeek1: forecastComparisonWeek1,
        comparisonWeek2: forecastComparisonWeek2,
        comparisonTableData,
        comparisonData,
        hotelNames: includedHotelNames,
      });
    } catch (e) {
      setError("Impossible de générer le fichier XLSX. Assurez-vous que le template est présent dans /public/GRILLES_INFO_TEMPLATE.xlsx.");
    }
  };

  return (
    <FlexColumnBox margin="auto" mt={5} alignItems="center" width="100%">
      <Box
        width="95%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        mb={3}
      >
        <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
          Grilles Info-Villégiature
        </Typography>
        <Box sx={{ position: "absolute", right: 0, display: "flex", gap: 1 }}>
          <Tooltip title="Exporter les données au format XLSX">
            <span>
              <IconButton 
                color="primary" 
                onClick={handleExportXlsx} 
                size="large"
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={4}
        flexWrap="wrap"
        justifyContent="center"
      >
        <HotelSelector
          hotels={hotels}
          selectedHotel={selectedHotel}
          onChange={(h) => setSelectedHotel(h)}
          disabled={user?.role !== "Admin" && !!user?.hotel}
          includeAllOption
        />
        <WeekDatePicker
          label="Début de la période de prévision"
          startDate={localStartDate}
          setStartDate={(date) => {
            const aligned = resetToFriday(date);
            setLocalStartDate(aligned);
            debouncedSetDate(aligned);
          }}
          onApply={(date) => {
            const aligned = resetToFriday(date);
            setLocalStartDate(aligned);
            applyDate(aligned);
          }}
        />
      </Box>

      {loading ? (
        <Box mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!error && weeklyOccupancyData.length === 0 && !comparisonTableData && (
            <Box mt={4}>
              <Alert severity="info">Aucune donnée pour la période sélectionnée.</Alert>
            </Box>
          )}
          {error && (
            <Box mt={4}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {!selectedHotel && hotelDataStatus.length > 0 && (
            <HotelDataStatusList hotels={hotelDataStatus} />
          )}

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            flexWrap="wrap"
            gap={3}
            mt={5}
            mb={5}
          >
            <DataComparisonGrid
              title={`Occupation prévue vs réelle - ${currentYear}`}
              leftSubtitle={`Prévision ${currentYear}`}
              rightSubtitle={`Réelle ${currentYear}`}
              leftData={comparisonData.forecastData.current}
              rightData={comparisonData.forecastData.previous}
            />
            <DataComparisonGrid
              title={`Occupation réelle ${currentYear} vs ${currentYear - 1}`}
              leftSubtitle={`Réelle ${currentYear}`}
              rightSubtitle={`Réelle ${currentYear - 1}`}
              leftData={comparisonData.realData.current}
              rightData={comparisonData.realData.previous}
            />
            <DataComparisonGrid
              title="Achalandage destination"
              leftSubtitle={`Visiteurs ${currentYear}`}
              rightSubtitle={`Visiteurs ${currentYear - 1}`}
              leftData={comparisonData.visitorData.current}
              rightData={comparisonData.visitorData.previous}
            />
          </Box>

          {weeklyForecastData.length > 0 && (
            <WeeklyForecastTable
                startDate={addDaysToDate(startDate, 14)}
                data={weeklyForecastData}
                currentYear={currentYear}
                comparisonWeek1={forecastComparisonWeek1}
                comparisonWeek2={forecastComparisonWeek2}
              />
          )}
        </>
      )}
    </FlexColumnBox>
  );
};

export default GrillesAVT;
